"use client"

import { iconsMap } from "@/public/icons/iconsMap"
import styled from "@emotion/styled"
import { Box } from "@mui/material"
import Image from "next/image"
import React from "react"
import { useInView } from "react-intersection-observer"
import { getNonUmbracoUrl } from "utils"
import { isVideoUrl } from "utils/mediaTypeDetection"
import { transitionDelayTime, useTransitionFadeInStyle } from ".."
import CommunityTexts from "../common/bit/CommunityTexts"
import { useIsDesktop } from "../common/bit/MediaQueries"
import BlockWrapper from "../common/BlockWrapper"
import { mediaQueries, sizes } from "../theme/theme"
import { useTransitionUpStyles } from "../transitions/useTransitionUpStyles"
import { useInViewConfig } from "../transitions/utils"
import VideoContainer from "./VideoContainer"

const GRAPHQL_URL = process.env.GRAPHQL_URL?.replace("/graphql", "").trim()

const bwSizes = sizes.BlockWrapper

const StyledBlockWrapper = styled(BlockWrapper)`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 60px;
  width: 100%;
  ${mediaQueries.tabletDown} {
    flex-direction: column;
  }
`

const ImagesWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "changeSides",
})<{ changeSides?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  ${mediaQueries.mobile} {
  }
  ${mediaQueries.tablet} {
    order: ${(props) => (props.changeSides ? 1 : 2)};
  }
  ${mediaQueries.desktopM} {
    order: ${(props) => (props.changeSides ? 1 : 2)};
  }
`
const BGImageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "changeSides",
})<{ changeSides?: boolean }>`
  display: flex;
  align-items: center;
  position: absolute;
  ${mediaQueries.mobile} {
    width: 100%;
    height: 330px;
    inset: auto 0 0 auto;
  }
  ${mediaQueries.tablet} {
    width: 100%;
    height: 330px;
    inset: auto 0 0 auto;
  }
  ${mediaQueries.desktopM} {
    inset: 0 auto auto 0px;
    width: ${(props) => (props.changeSides ? "1000px" : "750px")};
    height: ${(props) => (props.changeSides ? "1000px" : "515px")};
    left: ${(props) => (props.changeSides ? `-400px` : "unset")};
    transform: ${(props) => (props.changeSides ? "rotate(90deg)" : "unset")};
  }
  ${mediaQueries.desktopXl} {
    left: ${(props) =>
      props.changeSides ? `-${bwSizes.desktopM.padding}` : "unset"};
  }
`
const MainImageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "changeSides",
})<{ changeSides?: boolean }>`
  position: relative;
  z-index: 2;
  img {
    object-fit: contain;
  }
`

const StyledCommunityWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "changeSides",
})<{ changeSides?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 2;
  width: 100%;
  ${mediaQueries.mobile} {
    margin-top: ${sizes.baseSix};
    max-width: 100%;
  }
  ${mediaQueries.tablet} {
    margin-top: ${sizes.baseSix};
    max-width: 100%;
  }

  ${mediaQueries.desktopM} {
    margin-top: unset;
    order: ${(props) => (props.changeSides ? 2 : 1)};
  }
`

type TextMediaWithFlipProps = {
  data: any
  id?: string
}

const TextMediaWithFlip: React.FC<TextMediaWithFlipProps> = ({ data, id }) => {
  const {
    title,
    subtitle,
    text,
    changeSides,
    readMoreCheckbox,
    button,
    buttonPrevious,
    additionalButton,
    media,
    noBackground,
  } = data

  const isDesktop = useIsDesktop()
  const { ref: bgRef, inView: bgInView } = useInView(useInViewConfig)
  const { ref: mainRef, inView: mainInView } = useInView(useInViewConfig)

  const ditransitiveStyles = useTransitionUpStyles(
    mainInView,
    transitionDelayTime,
  )
  const BgImageTransition = useTransitionFadeInStyle(
    bgInView,
    transitionDelayTime,
  )
  const ArrowIcon = iconsMap.arrow_blue_white_line
  const currMedia = media

  // Prepare the CommunityTexts data object
  const communityTextsData = {
    title,
    subtitle,
    text,
    hasExpandButton: readMoreCheckbox?.value,
    button,
    buttonPrevious,
    additionalButton,
    ArrowIcon,
  }
  return (
    <StyledBlockWrapper id={id}>
      <StyledCommunityWrapper changeSides={changeSides?.value}>
        <CommunityTexts data={communityTextsData} />
      </StyledCommunityWrapper>
      <ImagesWrapper changeSides={changeSides?.value}>
        {!noBackground && (
          <BGImageContainer
            changeSides={changeSides?.value}
            ref={bgRef}
            style={{ ...BgImageTransition }}
          >
            <Image
              priority
              src={
                isDesktop
                  ? "/communityHachshara.svg"
                  : "/communityHachsharaMobile.svg"
              }
              alt="community Hachshara bg image"
              sizes="100vw"
              fill
            />
          </BGImageContainer>
        )}
        <MainImageContainer
          changeSides={changeSides?.value}
          ref={mainRef}
          style={{ ...ditransitiveStyles }}
        >
          {currMedia?.links?.[0] && currMedia.links[0].type === "MEDIA" ? (
            !isVideoUrl(currMedia.links[0].url) ? (
              <Image
                src={`${GRAPHQL_URL}${currMedia.links[0].url}`}
                alt="community Hachshara image"
                width={isDesktop ? 628 : 396}
                height={isDesktop ? 349 : 223}
              />
            ) : (
              <VideoContainer
                url={getNonUmbracoUrl(currMedia.links[0].url)}
                title={title?.value}
              />
            )
          ) : null}
        </MainImageContainer>
      </ImagesWrapper>
    </StyledBlockWrapper>
  )
}
export default TextMediaWithFlip
